import {
  Users, Briefcase, PiggyBank, Bot, Luggage, PartyPopper, Map, Plane, Gift,
  Backpack, FileCheck, Landmark, Home, Smartphone, HeartPulse, TrainFront, Sun
} from "lucide-react";

export const FEATURES = [
  {
    slug: "community",
    title: "Meet Your People",
    shortTitle: "Community",
    icon: Users,
    color: "#10B981",
    tagline: "Land in the UK already knowing people.",
    cardCopy: "Friendships that start before your flight takes off.",
    heroCopy: "The hardest part of moving abroad isn't the paperwork, it's the first lonely weeks. Castles members skip them entirely.",
    benefits: [
      "Get introduced to Indian students moving to your exact city",
      "Join your city circle before you fly, not after you land",
      "Flatmate-finding support from people who've done it",
      "A network that stays with you long after freshers' week",
    ],
    image: "https://images.unsplash.com/photo-1541475074124-af32f4cb0dbb",
  },
  {
    slug: "jobs",
    title: "Build Your Career",
    shortTitle: "Jobs & Careers",
    icon: Briefcase,
    color: "#0A1633",
    tagline: "Start earning sooner.",
    cardCopy: "CVs, job hunting and referrals, sorted from week one.",
    heroCopy: "Your first UK payslip shouldn't take months. Members get a proven playbook for part-time work and early career moves.",
    benefits: [
      "A UK-style CV that actually gets replies",
      "The 20-hour work rule explained simply",
      "Where to apply, what to say, what to avoid",
      "Referrals from members already working in your city",
    ],
    image: "https://images.pexels.com/photos/6549913/pexels-photo-6549913.jpeg",
  },
  {
    slug: "save-money",
    title: "Keep More of Your Money",
    shortTitle: "Save Money",
    icon: PiggyBank,
    color: "#10B981",
    tagline: "Keep more of your money for experiences that matter.",
    cardCopy: "Discounts and honest hacks that save hundreds a year.",
    heroCopy: "Every pound saved on essentials is a pound for weekends, travel and the moments you'll actually remember.",
    benefits: [
      "Member-only discounts on SIMs, food and travel",
      "The railcard, student discount and cashback playbook",
      "Accommodation support that avoids costly mistakes",
      "Real hacks from students living on real budgets",
    ],
    image: "https://images.unsplash.com/photo-1606757898266-5dcb04600340",
  },
  {
    slug: "castles-ai",
    title: "Castles AI",
    shortTitle: "Castles AI",
    icon: Bot,
    color: "#D4AF37",
    tagline: "A personal advisor who's done all of this before.",
    cardCopy: "Ask anything about UK student life, anytime.",
    heroCopy: "It's 1am and you're wondering about visas, banking or what to pack. Castles AI answers instantly, trained on real guidance from former international students.",
    benefits: [
      "Instant answers on packing, banking, visas and more",
      "Trained on Castles' own knowledge base first",
      "Knows your city, university and journey stage",
      "Always available, never judgemental",
    ],
    image: "https://images.unsplash.com/photo-1507812984078-917a274065be",
  },
  {
    slug: "before-you-fly",
    title: "Before You Fly",
    shortTitle: "Before You Fly",
    icon: Luggage,
    color: "#22C55E",
    tagline: "Arrive prepared, not panicked.",
    cardCopy: "Packing, documents and induction, done calmly.",
    heroCopy: "The weeks before your flight set the tone for your entire first term. Members arrive with everything handled.",
    benefits: [
      "The complete pre-flight induction, free for members",
      "Packing checklists written by students, not blogs",
      "Documents, TB tests, BRP, nothing forgotten",
      "Airport arrival walkthrough so day one feels easy",
    ],
    image: "https://images.unsplash.com/photo-1454496406107-dc34337da8d6",
  },
  {
    slug: "events",
    title: "Members Only Events",
    shortTitle: "Events",
    icon: PartyPopper,
    color: "#D4AF37",
    tagline: "Your social calendar, sorted before you land.",
    cardCopy: "City meetups, celebrations and inductions.",
    heroCopy: "Freshers' week is overwhelming. Castles events are the opposite: warm, familiar faces and instant belonging.",
    benefits: [
      "Free online inductions before you fly",
      "City meetups with students from your circle",
      "Festival celebrations that feel like home",
      "First events that turn strangers into flatmates",
    ],
    image: "https://images.unsplash.com/photo-1541475074124-af32f4cb0dbb",
  },
  {
    slug: "city-guides",
    title: "City Guides",
    shortTitle: "City Guides",
    icon: Map,
    color: "#0A1633",
    tagline: "Know your new city like a local, from day one.",
    cardCopy: "Written by students actually living there.",
    heroCopy: "Every UK city has its own rhythm: where to live, where to eat, what to avoid. Members get the inside version.",
    benefits: [
      "Area-by-area accommodation guidance",
      "Indian groceries, food spots and hidden gems",
      "Transport tricks that save time and money",
      "Updated by students living there right now",
    ],
    image: "https://images.unsplash.com/photo-1606757898266-5dcb04600340",
  },
  {
    slug: "weekend-adventures",
    title: "Weekend Adventures",
    shortTitle: "Weekend Trips",
    icon: Plane,
    color: "#10B981",
    tagline: "See the UK with people who feel like home.",
    cardCopy: "Trips and getaways with your new circle.",
    heroCopy: "Student life in the UK is about more than lectures. Members explore together: castles, coasts, cities and everything between.",
    benefits: [
      "Group trips organised by and for members",
      "Budget travel hacks for students",
      "New cities with people you already know",
      "Memories that make the UK feel like yours",
    ],
    image: "https://images.unsplash.com/photo-1507812984078-917a274065be",
  },
  {
    slug: "student-deals",
    title: "Student Deals",
    shortTitle: "Deals",
    icon: Gift,
    color: "#22C55E",
    tagline: "Exclusive deals that stretch every pound.",
    cardCopy: "Hand-picked offers, only for members.",
    heroCopy: "We negotiate member-only deals on the things students actually spend on, so your budget goes further without the effort.",
    benefits: [
      "Deals on food, travel, SIMs and essentials",
      "New offers added as the network grows",
      "No spam, only genuinely useful discounts",
      "The bigger the community, the better the deals",
    ],
    image: "https://images.pexels.com/photos/6549913/pexels-photo-6549913.jpeg",
  },
];

export const RESOURCES = [
  { icon: Backpack, title: "Packing Checklist", copy: "Exactly what to bring, and what to leave behind." },
  { icon: FileCheck, title: "Visa Tips", copy: "CAS, TB tests and paperwork explained calmly." },
  { icon: Landmark, title: "Student Banking", copy: "Open the right UK account in your first week." },
  { icon: Home, title: "Accommodation", copy: "Areas, contracts and deposits, without the traps." },
  { icon: Smartphone, title: "SIM Guides", copy: "The best-value UK SIMs for Indian students." },
  { icon: HeartPulse, title: "Healthcare", copy: "NHS registration, GP signups and what IHS covers." },
  { icon: TrainFront, title: "Transport", copy: "Railcards, buses and student travel hacks." },
  { icon: Sun, title: "UK Student Life", copy: "Weather, culture, food and settling in happily." },
];
